'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useChat } from '@/lib/hooks/useChat';
import { toast } from 'sonner';

// ============================================================
// Types
// ============================================================

interface SearchResult {
  intent: {
    destination?: string;
    product_types?: string[];
    traveller_count?: number;
    budget?: { min?: number; max?: number; currency?: string };
    confidence: number;
  };
  routing: Array<{
    product_type: string;
    selected_supplier: string;
    reason: string;
    confidence: number;
    fallback_chain: string[];
  }>;
}

interface AIPackage {
  package_name: string;
  destination: string;
  duration_days: number;
  total_price: number;
  currency: string;
  components: Array<{
    type: string;
    supplier: string;
    name: string;
    description: string;
    price: number;
    currency: string;
  }>;
  ai_rationale: string;
  upsell_suggestions: string[];
  cross_sell_suggestions: string[];
  highlights: string[];
}

interface AIItinerary {
  destination: string;
  duration_days: number;
  days: Array<{
    day: number;
    title: string;
    activities: Array<{ time: string; activity: string; supplier?: string; notes?: string }>;
    meals?: { breakfast?: string; lunch?: string; dinner?: string };
    accommodation?: string;
  }>;
  optimization_notes: string;
  weather_advisory: string;
  visa_advisory: string;
  local_insights: string;
  packing_tips: string[];
}

interface DestinationInsight {
  destination: string;
  overview: string;
  best_time_to_visit: string;
  weather_summary: string;
  visa_requirements: string;
  travel_advisory: string;
  local_insights: string;
  top_experiences: string[];
  recommended_suppliers: string[];
  budget_guide: string;
  cultural_tips: string[];
}

type ActiveTab = 'search' | 'packages' | 'itinerary' | 'destinations' | 'assistant';

const SUPPLIER_COLORS: Record<string, string> = {
  Amadeus: 'text-blue-400',
  Duffel: 'text-violet-400',
  Viator: 'text-emerald-400',
  RateHawk: 'text-amber-400',
  'G Adventures': 'text-orange-400',
  'Crystal Cruises': 'text-cyan-400',
  TripJack: 'text-rose-400',
  default: 'text-primary',
};

const COMPONENT_ICONS: Record<string, string> = {
  flight: 'PaperAirplaneIcon',
  hotel: 'BuildingOffice2Icon',
  activity: 'StarIcon',
  transfer: 'TruckIcon',
  insurance: 'ShieldCheckIcon',
  cruise: 'GlobeAltIcon',
};

// ============================================================
// AI Travel Intelligence Platform Content
// ============================================================

export default function AITravelIntelligenceContent() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [aiPackage, setAIPackage] = useState<AIPackage | null>(null);
  const [itinerary, setItinerary] = useState<AIItinerary | null>(null);
  const [destinationInsight, setDestinationInsight] = useState<DestinationInsight | null>(null);
  const [destination, setDestination] = useState('');
  const [durationDays, setDurationDays] = useState(7);
  const [travellerCount, setTravellerCount] = useState(2);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [chatInput, setChatInput] = useState('');

  const { response: chatResponse, isLoading: chatLoading, error: chatError, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    true
  );

  useEffect(() => {
    if (chatError) toast.error(chatError.message);
  }, [chatError]);

  useEffect(() => {
    if (chatResponse && !chatLoading) {
      setChatMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return [...prev.slice(0, -1), { role: 'assistant', content: chatResponse }];
        }
        return [...prev, { role: 'assistant', content: chatResponse }];
      });
    }
  }, [chatResponse, chatLoading]);

  const callIntelligence = useCallback(async (operation: string, payload: Record<string, unknown>) => {
    setLoading(true);
    try {
      const res = await fetch('/api/ai/travel-intelligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation, ...payload }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Operation failed');
      return data.data;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'AI operation failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const result = await callIntelligence('search', { query: searchQuery });
    if (result) setSearchResult(result);
  };

  const handleGeneratePackage = async () => {
    if (!destination.trim()) return;
    const result = await callIntelligence('generate_package', {
      destination,
      duration_days: durationDays,
      traveller_count: travellerCount,
    });
    if (result) setAIPackage(result.package);
  };

  const handlePlanItinerary = async () => {
    if (!destination.trim()) return;
    const result = await callIntelligence('plan_itinerary', {
      destination,
      duration_days: durationDays,
      traveller_count: travellerCount,
      preferences: { style: 'luxury', pace: 'moderate' },
    });
    if (result) setItinerary(result.itinerary);
  };

  const handleDestinationInsights = async () => {
    if (!destination.trim()) return;
    const result = await callIntelligence('destination_insights', { destination });
    if (result) setDestinationInsight(result.insights);
  };

  const handleChatSend = () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages((prev) => [...prev, { role: 'user', content: userMsg }]);

    const systemMsg = {
      role: 'system' as const,
      content: `You are the WorldwayLuxe AI Travel Assistant — an expert luxury travel advisor with deep knowledge of destinations, suppliers, booking processes, visa requirements, and travel planning. Help users plan their perfect luxury journey. Be specific, helpful, and suggest WorldwayLuxe services where relevant.`,
    };

    const history = chatMessages.map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));
    sendMessage([systemMsg, ...history, { role: 'user', content: userMsg }], { temperature: 0.7, max_tokens: 1500 });
  };

  const tabs: Array<{ id: ActiveTab; label: string; icon: string }> = [
    { id: 'search', label: 'AI Search', icon: 'MagnifyingGlassIcon' },
    { id: 'packages', label: 'Package Builder', icon: 'CubeIcon' },
    { id: 'itinerary', label: 'Itinerary Planner', icon: 'MapIcon' },
    { id: 'destinations', label: 'Destination Intel', icon: 'GlobeAltIcon' },
    { id: 'assistant', label: 'Travel Assistant', icon: 'SparklesIcon' },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <div className="border-b border-border px-6 py-5" style={{ background: 'var(--card)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 flex items-center justify-center">
              <Icon name="SparklesIcon" size={20} className="text-violet-400" />
            </div>
            <div>
              <h1 className="font-serif text-2xl font-bold text-foreground">AI Travel Intelligence Platform</h1>
              <p className="text-sm text-muted-foreground">Powered by Gemini AI · Natural language search · Multi-supplier intelligence</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-500/30 bg-green-500/10 text-xs text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Gemini Active
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground">
                <Icon name="ServerStackIcon" size={12} />
                15 Suppliers
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border px-6" style={{ background: 'var(--card)' }}>
        <div className="max-w-7xl mx-auto flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={15} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* ── AI Search Tab ─────────────────────────────────────── */}
        {activeTab === 'search' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-4">Natural Language Travel Search</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Describe your ideal trip in plain English. Gemini AI will parse your intent and route to the best suppliers.
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="e.g. Luxury 10-day safari in Kenya for 2 people in October, budget $15,000"
                  className="flex-1 px-4 py-3 rounded-xl border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                />
                <button
                  onClick={handleSearch}
                  disabled={loading || !searchQuery.trim()}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
                >
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="SparklesIcon" size={16} />}
                  Search
                </button>
              </div>

              {/* Quick prompts */}
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  'Luxury Maldives honeymoon for 2, 7 nights',
                  'Business class flights London to Tokyo',
                  'Private villa Bali with pool, 10 days',
                  'Antarctica expedition cruise December',
                ].map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => setSearchQuery(prompt)}
                    className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>

            {searchResult && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Parsed Intent */}
                <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="LightBulbIcon" size={18} className="text-amber-400" />
                    <h3 className="font-semibold text-foreground">Parsed Intent</h3>
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                      {Math.round((searchResult.intent.confidence || 0) * 100)}% confidence
                    </span>
                  </div>
                  <div className="space-y-2">
                    {searchResult.intent.destination && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Destination</span>
                        <span className="text-foreground font-medium">{searchResult.intent.destination}</span>
                      </div>
                    )}
                    {searchResult.intent.traveller_count && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Travellers</span>
                        <span className="text-foreground font-medium">{searchResult.intent.traveller_count}</span>
                      </div>
                    )}
                    {searchResult.intent.budget && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Budget</span>
                        <span className="text-foreground font-medium">
                          {searchResult.intent.budget.currency || 'USD'} {searchResult.intent.budget.min?.toLocaleString()} – {searchResult.intent.budget.max?.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {searchResult.intent.product_types && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Services</span>
                        <span className="text-foreground font-medium capitalize">{searchResult.intent.product_types.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Supplier Routing */}
                <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="CpuChipIcon" size={18} className="text-violet-400" />
                    <h3 className="font-semibold text-foreground">AI Supplier Routing</h3>
                  </div>
                  <div className="space-y-3">
                    {searchResult.routing.map((r, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-border/50 bg-surface-4">
                        <Icon
                          name={COMPONENT_ICONS[r.product_type] as Parameters<typeof Icon>[0]['name'] || 'CubeIcon'}
                          size={16}
                          className="text-primary mt-0.5 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-muted-foreground capitalize">{r.product_type}</span>
                            <span className={`text-xs font-semibold ${SUPPLIER_COLORS[r.selected_supplier] || SUPPLIER_COLORS.default}`}>
                              → {r.selected_supplier}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.reason}</p>
                          {r.fallback_chain?.length > 0 && (
                            <p className="text-xs text-muted-foreground/60 mt-0.5">
                              Fallback: {r.fallback_chain.slice(0, 2).join(', ')}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {Math.round((r.confidence || 0) * 100)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Package Builder Tab ───────────────────────────────── */}
        {activeTab === 'packages' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-4">AI Dynamic Package Generator</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-xs label-caps mb-2 block">Destination</label>
                  <input
                    type="text"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="e.g. Maldives, Kenya, Japan"
                    className="w-full px-3 py-2 rounded-xl border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="text-xs label-caps mb-2 block">Duration (days)</label>
                  <input
                    type="number"
                    value={durationDays}
                    onChange={(e) => setDurationDays(Number(e.target.value))}
                    min={1}
                    max={30}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-surface-4 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="text-xs label-caps mb-2 block">Travellers</label>
                  <input
                    type="number"
                    value={travellerCount}
                    onChange={(e) => setTravellerCount(Number(e.target.value))}
                    min={1}
                    max={20}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-surface-4 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
              </div>
              <button
                onClick={handleGeneratePackage}
                disabled={loading || !destination.trim()}
                className="flex items-center gap-2 px-6 py-3 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
              >
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="SparklesIcon" size={16} />}
                Generate AI Package
              </button>
            </div>

            {aiPackage && (
              <div className="rounded-2xl border border-primary/30 p-6" style={{ background: 'var(--card)' }}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-serif text-xl font-bold text-foreground">{aiPackage.package_name}</h3>
                    <p className="text-sm text-muted-foreground">{aiPackage.destination} · {aiPackage.duration_days} days · {travellerCount} travellers</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{aiPackage.currency} {aiPackage.total_price?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">total package</p>
                  </div>
                </div>

                {/* Highlights */}
                {aiPackage.highlights?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {aiPackage.highlights.map((h, i) => (
                      <span key={i} className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">{h}</span>
                    ))}
                  </div>
                )}

                {/* Components */}
                <div className="space-y-2 mb-4">
                  <p className="text-xs label-caps mb-2">Package Components</p>
                  {aiPackage.components?.map((comp, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-surface-4">
                      <Icon
                        name={COMPONENT_ICONS[comp.type] as Parameters<typeof Icon>[0]['name'] || 'CubeIcon'}
                        size={16}
                        className="text-primary flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{comp.name}</p>
                        <p className="text-xs text-muted-foreground">{comp.supplier} · {comp.description?.slice(0, 80)}</p>
                      </div>
                      <span className="text-sm font-semibold text-foreground flex-shrink-0">
                        {comp.currency} {comp.price?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>

                {/* AI Rationale */}
                <div className="p-4 rounded-xl border border-violet-500/20 bg-violet-500/5 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="SparklesIcon" size={14} className="text-violet-400" />
                    <span className="text-xs font-semibold text-violet-400">AI Rationale</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{aiPackage.ai_rationale}</p>
                </div>

                {/* Upsells & Cross-sells */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aiPackage.upsell_suggestions?.length > 0 && (
                    <div>
                      <p className="text-xs label-caps mb-2">Upgrade Opportunities</p>
                      <div className="space-y-1">
                        {aiPackage.upsell_suggestions.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Icon name="ArrowUpIcon" size={12} className="text-amber-400 flex-shrink-0" />
                            {s}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {aiPackage.cross_sell_suggestions?.length > 0 && (
                    <div>
                      <p className="text-xs label-caps mb-2">Add-On Services</p>
                      <div className="space-y-1">
                        {aiPackage.cross_sell_suggestions.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Icon name="PlusCircleIcon" size={12} className="text-emerald-400 flex-shrink-0" />
                            {s}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Itinerary Planner Tab ─────────────────────────────── */}
        {activeTab === 'itinerary' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-4">AI Itinerary Planner & Optimizer</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-xs label-caps mb-2 block">Destination</label>
                  <input
                    type="text"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="e.g. Kyoto, Japan"
                    className="w-full px-3 py-2 rounded-xl border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="text-xs label-caps mb-2 block">Duration (days)</label>
                  <input
                    type="number"
                    value={durationDays}
                    onChange={(e) => setDurationDays(Number(e.target.value))}
                    min={1}
                    max={30}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-surface-4 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="text-xs label-caps mb-2 block">Travellers</label>
                  <input
                    type="number"
                    value={travellerCount}
                    onChange={(e) => setTravellerCount(Number(e.target.value))}
                    min={1}
                    max={20}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-surface-4 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
              </div>
              <button
                onClick={handlePlanItinerary}
                disabled={loading || !destination.trim()}
                className="flex items-center gap-2 px-6 py-3 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
              >
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="MapIcon" size={16} />}
                Plan Itinerary
              </button>
            </div>

            {itinerary && (
              <div className="space-y-4">
                {/* Advisories */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { icon: 'SunIcon', label: 'Weather', text: itinerary.weather_advisory, color: 'text-amber-400' },
                    { icon: 'DocumentTextIcon', label: 'Visa', text: itinerary.visa_advisory, color: 'text-blue-400' },
                    { icon: 'LightBulbIcon', label: 'Local Insights', text: itinerary.local_insights, color: 'text-emerald-400' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-xl border border-border p-4" style={{ background: 'var(--card)' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={15} className={item.color} />
                        <span className="text-xs font-semibold text-foreground">{item.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>

                {/* Day-by-day */}
                <div className="space-y-3">
                  {itinerary.days?.map((day) => (
                    <div key={day.day} className="rounded-xl border border-border p-5" style={{ background: 'var(--card)' }}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-primary">{day.day}</span>
                        </div>
                        <h4 className="font-semibold text-foreground">{day.title}</h4>
                        {day.accommodation && (
                          <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
                            <Icon name="BuildingOffice2Icon" size={12} />
                            {day.accommodation}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2 ml-11">
                        {day.activities?.map((act, i) => (
                          <div key={i} className="flex items-start gap-3 text-sm">
                            <span className="text-xs text-muted-foreground w-14 flex-shrink-0 pt-0.5">{act.time}</span>
                            <div>
                              <span className="text-foreground">{act.activity}</span>
                              {act.supplier && <span className="text-xs text-primary ml-2">via {act.supplier}</span>}
                              {act.notes && <p className="text-xs text-muted-foreground mt-0.5">{act.notes}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Packing tips */}
                {itinerary.packing_tips?.length > 0 && (
                  <div className="rounded-xl border border-border p-4" style={{ background: 'var(--card)' }}>
                    <p className="text-xs label-caps mb-3">Packing Tips</p>
                    <div className="flex flex-wrap gap-2">
                      {itinerary.packing_tips.map((tip, i) => (
                        <span key={i} className="text-xs px-3 py-1 rounded-full border border-border text-muted-foreground">{tip}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Destination Intelligence Tab ──────────────────────── */}
        {activeTab === 'destinations' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-4">AI Destination Discovery & Intelligence</h2>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleDestinationInsights()}
                  placeholder="Enter destination (e.g. Maldives, Serengeti, Kyoto)"
                  className="flex-1 px-4 py-3 rounded-xl border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                />
                <button
                  onClick={handleDestinationInsights}
                  disabled={loading || !destination.trim()}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
                >
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="GlobeAltIcon" size={16} />}
                  Discover
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {['Maldives', 'Serengeti', 'Kyoto', 'Santorini', 'Patagonia', 'Bhutan'].map((d) => (
                  <button
                    key={d}
                    onClick={() => setDestination(d)}
                    className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {destinationInsight && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
                  <h3 className="font-serif text-xl font-bold text-foreground mb-2">{destinationInsight.destination}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{destinationInsight.overview}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { icon: 'CalendarDaysIcon', label: 'Best Time', text: destinationInsight.best_time_to_visit, color: 'text-amber-400' },
                      { icon: 'SunIcon', label: 'Weather', text: destinationInsight.weather_summary, color: 'text-orange-400' },
                      { icon: 'DocumentTextIcon', label: 'Visa', text: destinationInsight.visa_requirements, color: 'text-blue-400' },
                      { icon: 'ShieldCheckIcon', label: 'Travel Advisory', text: destinationInsight.travel_advisory, color: 'text-green-400' },
                      { icon: 'LightBulbIcon', label: 'Local Insights', text: destinationInsight.local_insights, color: 'text-violet-400' },
                      { icon: 'BanknotesIcon', label: 'Budget Guide', text: destinationInsight.budget_guide, color: 'text-emerald-400' },
                    ].map((item) => (
                      <div key={item.label} className="p-4 rounded-xl border border-border/50 bg-surface-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} className={item.color} />
                          <span className="text-xs font-semibold text-foreground">{item.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {destinationInsight.top_experiences?.length > 0 && (
                    <div className="rounded-xl border border-border p-4" style={{ background: 'var(--card)' }}>
                      <p className="text-xs label-caps mb-3">Top Experiences</p>
                      <div className="space-y-2">
                        {destinationInsight.top_experiences.map((exp, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="StarIcon" size={12} className="text-amber-400 flex-shrink-0" />
                            {exp}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {destinationInsight.cultural_tips?.length > 0 && (
                    <div className="rounded-xl border border-border p-4" style={{ background: 'var(--card)' }}>
                      <p className="text-xs label-caps mb-3">Cultural Tips</p>
                      <div className="space-y-2">
                        {destinationInsight.cultural_tips.map((tip, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="InformationCircleIcon" size={12} className="text-blue-400 flex-shrink-0" />
                            {tip}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Travel Assistant Tab ──────────────────────────────── */}
        {activeTab === 'assistant' && (
          <div className="rounded-2xl border border-border overflow-hidden" style={{ background: 'var(--card)' }}>
            <div className="px-6 py-4 border-b border-border flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500/20 to-yellow-600/20 flex items-center justify-center">
                <span className="text-amber-400 text-sm">✦</span>
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm">AI Travel Assistant</h3>
                <p className="text-xs text-muted-foreground">Conversational booking · Modifications · Destination advice</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5 text-xs text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Online
              </div>
            </div>

            {/* Messages */}
            <div className="h-96 overflow-y-auto p-6 space-y-4">
              {chatMessages.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500/20 to-yellow-600/20 flex items-center justify-center mx-auto mb-3">
                    <span className="text-amber-400 text-xl">✦</span>
                  </div>
                  <p className="text-sm font-medium text-foreground mb-1">WorldwayLuxe AI Travel Assistant</p>
                  <p className="text-xs text-muted-foreground mb-4">Ask me anything about travel planning, bookings, destinations, or modifications</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {[
                      'Plan a luxury safari in Kenya',
                      'Best time to visit Maldives?',
                      'Help me modify my booking',
                      'Visa requirements for Japan',
                    ].map((prompt) => (
                      <button
                        key={prompt}
                        onClick={() => setChatInput(prompt)}
                        className="text-xs px-3 py-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs ${
                    msg.role === 'user' ? 'bg-primary/20 text-primary' : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {msg.role === 'user' ? <Icon name="UserCircleIcon" size={18} /> : '✦'}
                  </div>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user' ?'bg-primary/15 text-foreground rounded-tr-sm' :'border border-border/60 text-foreground rounded-tl-sm'
                  }`} style={msg.role === 'assistant' ? { background: 'var(--surface-4)' } : {}}>
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  </div>
                </div>
              ))}

              {chatLoading && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 text-amber-400 text-xs">✦</div>
                  <div className="flex gap-1 items-center px-4 py-3 rounded-2xl rounded-tl-sm border border-border/60" style={{ background: 'var(--surface-4)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="px-6 py-4 border-t border-border">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleChatSend()}
                  placeholder="Ask about destinations, bookings, modifications..."
                  className="flex-1 px-4 py-2.5 rounded-xl border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                  disabled={chatLoading}
                />
                <button
                  onClick={handleChatSend}
                  disabled={chatLoading || !chatInput.trim()}
                  className="px-4 py-2.5 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
                >
                  <Icon name="PaperAirplaneIcon" size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
